/**
 * Patients directory (design 3E): find/view/edit records — not first-call
 * booking, and the page says so. Numbered server paging (ZOY-285).
 *
 * Every field the directory shows is its own column, and every filter and
 * sort is a SERVER query over the whole clinic-scoped set (patientDirectory
 * compiles to the backend allow-list): file number, age or age range (a
 * date-of-birth window), caregiver name under the contacts key, Guest /
 * Patient by registration state, and the record's mobile — ANDed with the
 * toolbar's free search. Nothing filters or reorders one fetched page.
 * The free search keeps its scope ladder (file number → mobile → last name
 * → first name): the DSL has no cross-field OR, so it walks complete server
 * queries and only a real zero advances (ZOY-336/346).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Machine, Notice, Segmented, TextField } from '@/ui/kit';
import {
  Column,
  HeadButton,
  MobileOnly,
  Pager,
  PrimaryCell,
  RowButton,
  TableSearch,
  WorkTable,
} from '@/ui/WorkTable';
import { useNumberedList } from '@/core/api/usePagedList';
import { useDebounced } from '@/core/api/useDebounced';
import type { QueryFilter } from '@/core/api/querySpec';
import { listPatients, type PatientView } from '@/core/clinic/patientsApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import {
  ageFromDob,
  classifyResolutionInput,
  mobileContainsTerm,
  primaryContact,
  recordNumberFromInput,
} from './lifecycle';
import {
  DEFAULT_SORT,
  EMPTY_FILTERS,
  PHONE_FIELDS,
  activeChips,
  compileDirectoryFilters,
  isFilterActive,
  parseAge,
  sortSpec,
  todayIso,
  toggleSort,
  withoutChip,
  type ChipId,
  type DirectoryFilters,
  type DirectorySort,
  type PhoneField,
  type SortKey,
} from './patientDirectory';
import { RegistrationChip } from './RegistrationChip';
import { AutismTag } from './AutismTag';

type T = (id: string, v?: Record<string, string | number>) => string;
type Panel = 'record' | 'age' | 'caregiver' | 'status' | 'contact' | 'autism';

const Dash = () => <span style={{ color: 'var(--zc-faint)' }}>—</span>;

/** The one server filter a search rung means (ZOY-336/346; ZOY-705 home/alt). */
function scopeFilters(scope: string | undefined, term: string): QueryFilter[] {
  if (scope === 'record') {
    const n = recordNumberFromInput(term);
    return n === null ? [] : [{ field: 'recordNumber', op: 'eq', value: n }];
  }
  if (scope === 'contact')
    return [{ field: 'contactMobile', op: 'contains', value: mobileContainsTerm(term) }];
  if (scope === 'home')
    return [{ field: 'homePhone', op: 'contains', value: mobileContainsTerm(term) }];
  if (scope === 'alt')
    return [{ field: 'altPhone', op: 'contains', value: mobileContainsTerm(term) }];
  if (scope === 'caregiver')
    return [{ field: 'caregiverMobile', op: 'contains', value: mobileContainsTerm(term) }];
  if (scope === 'last') return [{ field: 'lastName', op: 'contains', value: term.trim() }];
  if (scope === 'first') return [{ field: 'firstName', op: 'contains', value: term.trim() }];
  return [];
}

export function PatientsListPage() {
  const intl = useIntl();
  const t: T = (id, v) => intl.formatMessage({ id }, v) as string;
  const authz = useAuthz();
  const canRegister = authz.can('clinic.patients.register');
  const canContacts = authz.can('clinic.patients.contacts.read');
  const num = (n: number) => intl.formatNumber(n, { useGrouping: false });

  // ── free search (scope ladder) ─────────────────────────────────────────
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  const kind = classifyResolutionInput(debounced);
  const scopes = useMemo<readonly string[]>(() => {
    if (kind === 'record') return ['record'];
    // ZOY-705: the record's own numbers first (contact, home, additional),
    // then a caregiver's.
    if (kind === 'phone')
      return canContacts ? ['contact', 'home', 'alt', 'caregiver'] : ['contact', 'home', 'alt'];
    if (kind === 'name') return ['last', 'first'];
    return [];
  }, [kind, canContacts]);

  // ── column filters and sort — all server-side ──────────────────────────
  const [filters, setFilters] = useState<DirectoryFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<DirectorySort>(DEFAULT_SORT);
  const [panel, setPanel] = useState<Panel | null>(null);
  const today = todayIso();
  const sortQuery = useMemo(() => sortSpec(sort), [sort]);

  /**
   * ZOY-705: the contract ANDs filters, so ONE request cannot ask "any of
   * the record's numbers". Every candidate filter set — a search scope
   * crossed with the record's phone fields when the Contact filter is on —
   * is one rung; the list shows the first rung with a result and pages THAT
   * server set. Every other rung is fetched at the same time as the first
   * (below), so stepping down lands in a warm cache: one round trip.
   */
  const hasPhoneFilter = filters.phone.trim() !== '';
  const phoneRungs = useMemo<readonly PhoneField[]>(
    () => (hasPhoneFilter ? PHONE_FIELDS : ['contactMobile']),
    [hasPhoneFilter],
  );
  const rungs = useMemo(() => {
    const out: { scope: string | undefined; phoneField: PhoneField }[] = [];
    for (const scope of scopes.length === 0 ? [undefined] : scopes)
      for (const phoneField of phoneRungs) out.push({ scope, phoneField });
    return out;
  }, [scopes, phoneRungs]);
  const filtersFor = useCallback(
    (r: { scope: string | undefined; phoneField: PhoneField }): QueryFilter[] => [
      ...scopeFilters(r.scope, debounced),
      ...compileDirectoryFilters(filters, canContacts, today, r.phoneField),
    ],
    [debounced, filters, canContacts, today],
  );
  const rungKey = JSON.stringify([rungs, debounced, filters.phone]);
  const [rungIdx, setRungIdx] = useState(0);
  useEffect(() => setRungIdx(0), [rungKey]);
  const rung = rungs[Math.min(rungIdx, rungs.length - 1)]!;
  const allFilters = useMemo(() => filtersFor(rung), [filtersFor, rung]);

  // Filter and sort state live in React state, and useNumberedList keys its
  // query on them — so they travel with every page, and a change returns to
  // page 1. The pager's total is the server's for THIS filtered set.
  const list = useNumberedList<PatientView>({
    keyBase: ['patients', authz.clinicId],
    fetchPage: (spec) => listPatients(spec),
    filters: allFilters,
    sort: sortQuery,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (rungs.length < 2) return;
    for (const r of rungs.slice(1)) {
      const rungFilters = filtersFor(r);
      void queryClient.prefetchQuery({
        queryKey: [
          'patients',
          authz.clinicId,
          JSON.stringify([rungFilters, sortQuery, list.perPage]),
          1,
        ],
        queryFn: () =>
          listPatients({ filters: rungFilters, sort: sortQuery, limit: list.perPage, page: 1 }),
        staleTime: 30_000,
      });
    }
  }, [rungs, filtersFor, sortQuery, list.perPage, authz.clinicId, queryClient]);

  useEffect(() => {
    if (
      rungIdx < rungs.length - 1 &&
      !list.loading &&
      list.error === null &&
      list.data !== undefined &&
      list.data.total === 0
    ) {
      setRungIdx((i) => i + 1);
    }
  }, [rungIdx, rungs.length, list.loading, list.error, list.data]);

  const chips = activeChips(filters);

  // ── header affordances ─────────────────────────────────────────────────
  const sortBtn = (key: SortKey, col: string) => {
    const on = sort.key === key;
    return (
      <HeadButton
        on={on}
        aria-label={t('pt.sortBy', { col })}
        aria-pressed={on}
        title={on ? t(sort.dir === 'asc' ? 'pt.sortedAsc' : 'pt.sortedDesc') : undefined}
        onClick={() => setSort((s) => toggleSort(s, key))}
      >
        {on ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
      </HeadButton>
    );
  };
  const filterBtn = (id: Panel, col: string, active: boolean) => (
    <HeadButton
      on={active || panel === id}
      aria-label={t('pt.filterBy', { col })}
      aria-expanded={panel === id}
      onClick={() => setPanel((p) => (p === id ? null : id))}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M3 5h18l-7 8v6l-4 2v-8z" />
      </svg>
      {active && <span aria-hidden>•</span>}
    </HeadButton>
  );
  const head = (label: string, controls: React.ReactNode) => (
    <>
      <span style={{ minInlineSize: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </span>
      {controls}
    </>
  );

  const colRecord = t('pt.colRecord');
  const colPatient = t('pt.colPatient');
  const colAge = t('pt.colAge');
  const colCaregiver = t('pt.colCaregiver');
  const colStatus = t('pt.colStatus');
  const colContact = t('pt.colContact');
  const colAutism = t('pt.colAutism');

  const columns: Column[] = [
    {
      id: 'record',
      label: colRecord,
      width: 'auto',
      tier: 'tablet-up',
      header: head(
        colRecord,
        <>
          {sortBtn('record', colRecord)}
          {filterBtn('record', colRecord, filters.record !== null)}
        </>,
      ),
    },
    {
      id: 'patient',
      label: colPatient,
      width: '1.6fr',
      header: head(colPatient, sortBtn('patient', colPatient)),
    },
    {
      id: 'age',
      label: colAge,
      width: 'auto',
      tier: 'tablet-up',
      header: head(
        colAge,
        <>
          {sortBtn('age', colAge)}
          {filterBtn('age', colAge, filters.ageMin !== null || filters.ageMax !== null)}
        </>,
      ),
    },
    ...(canContacts
      ? [
          {
            id: 'caregiver',
            label: colCaregiver,
            width: '1fr',
            tier: 'desktop-only' as const,
            header: head(
              colCaregiver,
              filterBtn('caregiver', colCaregiver, filters.caregiver.trim() !== ''),
            ),
          },
        ]
      : []),
    {
      id: 'status',
      label: colStatus,
      width: 'auto',
      tier: 'tablet-up',
      header: head(colStatus, filterBtn('status', colStatus, filters.status !== 'all')),
    },
    {
      id: 'contact',
      label: colContact,
      width: '1fr',
      tier: 'desktop-only',
      header: head(colContact, filterBtn('contact', colContact, filters.phone.trim() !== '')),
    },
    {
      id: 'autism',
      label: colAutism,
      width: 'auto',
      tier: 'tablet-up',
      header: head(colAutism, filterBtn('autism', colAutism, filters.autism !== 'all')),
    },
    { id: 'actions', label: t('staff.colActions'), width: 'auto', align: 'end' },
  ];

  const chipLabel = (id: ChipId): string => {
    switch (id) {
      case 'record':
        return t('pt.chipRecord', { n: num(filters.record ?? 0) });
      case 'age':
        return filters.ageMin !== null && filters.ageMax !== null
          ? t('pt.chipAge', { min: num(filters.ageMin), max: num(filters.ageMax) })
          : filters.ageMin !== null
            ? t('pt.chipAgeMin', { n: num(filters.ageMin) })
            : t('pt.chipAgeMax', { n: num(filters.ageMax ?? 0) });
      case 'caregiver':
        return t('pt.chipCaregiver', { q: filters.caregiver.trim() });
      case 'status':
        return t(filters.status === 'guest' ? 'pt.chipGuest' : 'pt.chipPatient');
      case 'phone':
        // The number that answered is named when it is not the contact mobile.
        return rung.phoneField === 'contactMobile'
          ? t('pt.chipPhone', { q: filters.phone.trim() })
          : t('pt.chipPhoneVia', {
              q: filters.phone.trim(),
              kind: t(`pt.fPhoneKind.${rung.phoneField}`),
            });
      case 'autism':
        return t('pt.chipAutism');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div className={a.pageHead} style={{ marginBlockEnd: 0 }}>
          <h1 className={a.pageTitle}>{t('pt.title')}</h1>
          <p className={a.pageSub}>{t('pt.sub')}</p>
        </div>
        {canRegister && (
          <Link to="/patients/new">
            <Button variant="primary">{t('pt.new')}</Button>
          </Link>
        )}
      </div>

      {kind === 'phone' && !canContacts && (
        <Notice kind="info">{t('pt.phoneSearchNeedsContacts')}</Notice>
      )}

      <WorkTable<PatientView>
        aria-label={t('pt.title')}
        columns={columns}
        rows={list.view.rows}
        rowKey={(p) => p.id}
        loading={list.loading}
        error={list.error !== null}
        onRetry={list.refetch}
        toolbar={
          <>
            <TableSearch value={search} onChange={setSearch} placeholder={t('pt.searchPh')} />
            {chips.length > 0 && (
              <span
                role="group"
                aria-label={t('pt.activeFilters')}
                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
              >
                {chips.map((id) => (
                  <Chip key={id} kind="info">
                    {chipLabel(id)}
                    <button
                      type="button"
                      aria-label={t('pt.removeFilter', { label: chipLabel(id) })}
                      onClick={() => setFilters((f) => withoutChip(f, id))}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: '0 0 0 4px',
                        cursor: 'pointer',
                        color: 'inherit',
                        font: 'inherit',
                      }}
                    >
                      ×
                    </button>
                  </Chip>
                ))}
                <Button variant="link" onClick={() => setFilters(EMPTY_FILTERS)}>
                  {t('pt.clearAll')}
                </Button>
              </span>
            )}
            {panel && (
              <FilterPanel
                panel={panel}
                filters={filters}
                onApply={(next) => {
                  setFilters(next);
                  setPanel(null);
                }}
                onClose={() => setPanel(null)}
                t={t}
              />
            )}
          </>
        }
        empty={{
          title: t(debounced || isFilterActive(filters) ? 'table.noResultsTitle' : 'pt.emptyTitle'),
          body: t(debounced || isFilterActive(filters) ? 'table.noResultsBody' : 'pt.emptyBody'),
        }}
        renderCell={(p, col) => {
          const age = ageFromDob(p.dateOfBirth);
          const contact = primaryContact(p.caregivers);
          const mobile = contact?.mobile ?? p.contactMobile ?? null;
          switch (col) {
            case 'record':
              return p.recordNumber !== null ? <Machine>{num(p.recordNumber)}</Machine> : <Dash />;
            case 'patient':
              return (
                <PrimaryCell
                  avatar={`${p.firstName.slice(0, 1)}${p.lastName.slice(0, 1)}`}
                  main={`${p.firstName} ${p.lastName}`}
                  sub={
                    <MobileOnly>
                      <RegistrationChip state={p.registrationState} recordNumber={null} compact />
                      {age !== null && <span>{t('pt.ageShort', { n: age })}</span>}
                      {mobile && <Machine>{mobile}</Machine>}
                    </MobileOnly>
                  }
                />
              );
            case 'age':
              return age !== null ? <span>{t('pt.ageShort', { n: age })}</span> : <Dash />;
            case 'caregiver':
              return contact ? <span dir="auto">{contact.name}</span> : <Dash />;
            case 'status':
              return <RegistrationChip state={p.registrationState} recordNumber={null} compact />;
            case 'contact':
              return mobile ? <Machine>{mobile}</Machine> : <Dash />;
            case 'autism':
              // Only the affirmative reads as a tag; "not identified" is a quiet dash.
              return p.autismIdentified ? <AutismTag identified compact /> : <Dash />;
            case 'actions':
              return (
                <span style={{ display: 'flex', gap: 8 }}>
                  <Link to="/patients/$patientId" params={{ patientId: p.id }}>
                    <RowButton>{t('common.view')}</RowButton>
                  </Link>
                  {authz.can('clinic.patients.update') && (
                    <Link to="/patients/$patientId/edit" params={{ patientId: p.id }}>
                      <RowButton>{t('common.edit')}</RowButton>
                    </Link>
                  )}
                </span>
              );
            default:
              return null;
          }
        }}
        footer={
          <Pager
            view={list.view}
            perPage={list.perPage}
            onPerPage={list.setPerPage}
            onPage={list.goTo}
          />
        }
      />
      <Notice kind="info">{t('pt.twoStates')}</Notice>
    </div>
  );
}

/* ------------------------------ filter panel ------------------------------ */

/**
 * One column's filter, opened from its header and applied as a whole — a
 * deliberate Apply rather than per-keystroke queries, since every value is
 * a full server query over the directory. Enter applies; Cancel keeps the
 * previous filter untouched.
 */
function FilterPanel(props: {
  panel: Panel;
  filters: DirectoryFilters;
  onApply: (next: DirectoryFilters) => void;
  onClose: () => void;
  t: T;
}) {
  const { panel, filters, t } = props;
  const [record, setRecord] = useState(filters.record === null ? '' : String(filters.record));
  const [ageMin, setAgeMin] = useState(filters.ageMin === null ? '' : String(filters.ageMin));
  const [ageMax, setAgeMax] = useState(filters.ageMax === null ? '' : String(filters.ageMax));
  const [caregiver, setCaregiver] = useState(filters.caregiver);
  const [status, setStatus] = useState(filters.status);
  const [phone, setPhone] = useState(filters.phone);
  const [autism, setAutism] = useState(filters.autism);

  const recordN = record.trim() === '' ? null : recordNumberFromInput(record);
  const minN = ageMin.trim() === '' ? null : parseAge(ageMin);
  const maxN = ageMax.trim() === '' ? null : parseAge(ageMax);
  const recordBad = record.trim() !== '' && recordN === null;
  const ageBad =
    (ageMin.trim() !== '' && minN === null) ||
    (ageMax.trim() !== '' && maxN === null) ||
    (minN !== null && maxN !== null && minN > maxN);

  const apply = () => {
    if (panel === 'record' && recordBad) return;
    if (panel === 'age' && ageBad) return;
    props.onApply({
      ...filters,
      ...(panel === 'record' ? { record: recordN } : {}),
      ...(panel === 'age' ? { ageMin: minN, ageMax: maxN } : {}),
      ...(panel === 'caregiver' ? { caregiver } : {}),
      ...(panel === 'status' ? { status } : {}),
      ...(panel === 'contact' ? { phone } : {}),
      ...(panel === 'autism' ? { autism } : {}),
    });
  };

  return (
    <form
      style={{
        inlineSize: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 10,
        paddingBlockStart: 4,
      }}
      onSubmit={(e) => {
        e.preventDefault();
        apply();
      }}
    >
      {panel === 'record' && (
        <TextField
          label={t('pt.fRecord')}
          value={record}
          onChange={setRecord}
          help={t('pt.fRecordHelp')}
          error={recordBad ? t('pt.fRecordHelp') : undefined}
          machine
        />
      )}
      {panel === 'age' && (
        <>
          <TextField label={t('pt.fAgeMin')} value={ageMin} onChange={setAgeMin} machine />
          <TextField
            label={t('pt.fAgeMax')}
            value={ageMax}
            onChange={setAgeMax}
            help={t('pt.fAgeHelp')}
            error={ageBad ? t('pt.fAgeHelp') : undefined}
            machine
          />
        </>
      )}
      {panel === 'caregiver' && (
        <TextField label={t('pt.fCaregiver')} value={caregiver} onChange={setCaregiver} />
      )}
      {panel === 'status' && (
        <Segmented
          label={t('pt.fStatus')}
          options={[
            { value: 'all', label: t('pt.fStatusAll') },
            { value: 'patient', label: t('pt.fStatusPatient') },
            { value: 'guest', label: t('pt.fStatusGuest') },
          ]}
          value={status}
          onChange={setStatus}
        />
      )}
      {panel === 'autism' && (
        <Segmented
          label={t('pt.fAutism')}
          options={[
            { value: 'all', label: t('pt.fAutismAll') },
            { value: 'yes', label: t('pt.fAutismYes') },
          ]}
          value={autism}
          onChange={setAutism}
        />
      )}
      {panel === 'contact' && (
        <TextField
          label={t('pt.fPhone')}
          value={phone}
          onChange={setPhone}
          help={t('pt.fPhoneHelp')}
          machine
        />
      )}
      <span style={{ display: 'flex', gap: 8 }}>
        <Button
          type="submit"
          variant="primary"
          disabled={(panel === 'record' && recordBad) || (panel === 'age' && ageBad)}
        >
          {t('pt.apply')}
        </Button>
        <Button type="button" variant="quiet" onClick={props.onClose}>
          {t('common.cancel')}
        </Button>
      </span>
    </form>
  );
}
